"use client";

import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Label } from "@/app/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { QuantitySlider } from "@/app/components/ui/quantity-slider";
import { FileText, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CopyButton } from "@/app/components/ui/copy-button";

export default function LoremIpsumGeneratorTool() {
  const [type, setType] = useState("paragraphs");
  const [count, setCount] = useState(3);
  const [generatedText, setGeneratedText] = useState("");

  const loremWords = [
    "lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit", "sed", "do",
    "eiusmod", "tempor", "incididunt", "ut", "labore", "et", "dolore", "magna", "aliqua", "enim",
    "ad", "minim", "veniam", "quis", "nostrud", "exercitation", "ullamco", "laboris", "nisi",
    "aliquip", "ex", "ea", "commodo", "consequat", "duis", "aute", "irure", "in", "reprehenderit",
    "voluptate", "velit", "esse", "cillum", "fugiat", "nulla", "pariatur", "excepteur", "sint",
    "occaecat", "cupidatat", "non", "proident", "sunt", "culpa", "qui", "officia", "deserunt",
    "mollit", "anim", "id", "est", "laborum"
  ];

  const generateLoremIpsum = () => {
    let text = "";
    
    if (type === "words") {
      for (let i = 0; i < count; i++) {
        if (i > 0) text += " ";
        text += loremWords[Math.floor(Math.random() * loremWords.length)];
      }
    } else if (type === "sentences") {
      for (let i = 0; i < count; i++) {
        if (i > 0) text += " ";
        const sentenceLength = Math.floor(Math.random() * 10) + 5; // 5-15 words
        for (let j = 0; j < sentenceLength; j++) {
          if (j > 0) text += " ";
          text += loremWords[Math.floor(Math.random() * loremWords.length)];
        }
        text += ".";
      }
    } else if (type === "paragraphs") {
      for (let i = 0; i < count; i++) {
        if (i > 0) text += "\n\n";
        const paragraphLength = Math.floor(Math.random() * 5) + 3; // 3-8 sentences
        for (let j = 0; j < paragraphLength; j++) {
          if (j > 0) text += " ";
          const sentenceLength = Math.floor(Math.random() * 10) + 5; // 5-15 words
          for (let k = 0; k < sentenceLength; k++) {
            if (k > 0) text += " ";
            text += loremWords[Math.floor(Math.random() * loremWords.length)];
          }
          text += ".";
        }
      }
    }

    setGeneratedText(text);
    toast.success("Lorem ipsum text generated successfully!");
  };

  const clearAll = () => {
    setGeneratedText("");
    toast.info("Text cleared");
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Lorem Ipsum Generator
            </CardTitle>
            <CardDescription>
              Generate placeholder text for design and development
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Text Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="words">Words</SelectItem>
                  <SelectItem value="sentences">Sentences</SelectItem>
                  <SelectItem value="paragraphs">Paragraphs</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <QuantitySlider
              label="Count"
              value={count}
              min={1}
              max={20}
              step={1}
              onValueChange={setCount}
              hint="1–20 items"
            />

            <div className="flex gap-2">
              <Button onClick={generateLoremIpsum}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate Text
              </Button>
              <Button onClick={clearAll} variant="outline">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Generated Text</CardTitle>
            <CardDescription>
              Your Lorem ipsum text will appear here
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Output</Label>
              <Textarea
                value={generatedText}
                readOnly
                className="min-h-[300px] font-mono text-sm bg-muted/50"
                placeholder="Generated Lorem ipsum text will appear here..."
              />
            </div>

            {generatedText && (
              <CopyButton
                text={generatedText}
                variant="default"
                size="default"
                className="w-full"
                successMessage="Text copied!"
              >
                Copy Text
              </CopyButton>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Lorem Ipsum Information</CardTitle>
          <CardDescription>
            Learn about Lorem ipsum placeholder text
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Words</div>
              <div className="text-sm text-muted-foreground">Generate individual words</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Sentences</div>
              <div className="text-sm text-muted-foreground">Generate complete sentences</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Paragraphs</div>
              <div className="text-sm text-muted-foreground">Generate full paragraphs</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
